import {LogLevel, Occupancy} from "@variocube/app-api";
import {
	BoxAccess,
	PendingView,
	useBoxes,
	usePlatform,
	usePreventDeactivate,
	useStorageItemValue,
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {DateTime} from "luxon";
import {BackendApiCredentials, CubeFeatures, DeliveryOccupancyContent, SignatureOutputFormat} from "../types";
import {useCarrierId, useCubeCarrier} from "../CarrierProvider";
import {SignatureView, StrokeWidth} from "./SignatureInputView";
import {RecipientNameView} from './RecipientNameView';
import {PackageReleaseCodeView} from "./PackageReleaseCodeView";
import {CustomerPickupEventHandler} from "./customer-pickup-event-handlers";
import {useSetMessage} from "../DialogMessageProvider";
import {useLocalization} from "../../i18n";
import {ReUseContainerView} from "./ReUseContainerView";
import {v4 as uuid} from "uuid";


interface CustomerPickupProps {
    backendApiCredentials?: BackendApiCredentials;
    occupancies: Occupancy[];
    onDone: () => void;
}


enum CustomerPickupSteps {
    CustomerName,
    PackageReleaseCode,
    Signature,
    ReUseContainer,
    BoxAccess,
}

export function CustomerPickup({occupancies, onDone, backendApiCredentials}: CustomerPickupProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const boxes = useBoxes();
    const occupiedBoxes = useMemo(() => boxes.filter(box => occupancies.filter(o => o.boxNumber === box.number).length > 0), [boxes, occupancies]);
    const [recipientName, setRecipientName] = useState("");
    const [packageReleaseCodeLoading, setPackageReleaseCodeLoading] = useState<boolean>();
    const [signature, setSignature] = useState("");
    const [takeContainer, setTakeContainer] = useState<boolean>(true);

    const setMessage = useSetMessage();
    const customerPickupEventHandler = useMemo(() => new CustomerPickupEventHandler(), []);
    const carrierId = useCarrierId();
    const carrier = useCubeCarrier();

    const recipientNameRequired = useMemo<boolean>(() => {
        if(carrier) {
            return !!carrier.lastMile?.recipientNameRequired;
        }
        return false;
    }, [carrier]);

    const [step, setStep] = useState(recipientNameRequired ? CustomerPickupSteps.CustomerName : CustomerPickupSteps.Signature);

	const {headless} = useStorageItemValue<CubeFeatures>("features") ?? {};

	useEffect(() => {
		if (headless) {
			setStep(CustomerPickupSteps.BoxAccess);
		}
	}, [headless]);

    usePreventDeactivate();

    const handleDone = useCallback(async () => {
        if(takeContainer) {
            console.log(`Customer does not leave items in locker, ending ${occupancies.length} occupancies`);
            await Promise.all(occupancies.map(async occupancy => {
                try {
                    await platform.endOccupancy(occupancy.uuid, {
                        gracePeriod: takeContainer ? 180 : 0,
                        content: {
                            releasedAt: DateTime.utc().toISO(),
                            signature,
                            recipientName,
                        },
                        merge: true,
                    });
                    await platform.log(LogLevel.Audit, "Customer picked up", {occupancy});
                } catch (error) {
                    console.error("Error ending occupancy", error);
                }
            }));
        }else {
            console.log(`Customer wants to leave re-use container in locker, updating ${occupancies.length} occupancies`);
            for(let occupancy of occupancies) {
                await platform.updateOccupancy(occupancy.uuid, {
                    barCode: uuid(),
                    carrierId: carrierId,
                    storedAt: DateTime.utc().toISO(),
                    sender: {},
                    relatedId: occupancy.content.id,
                    signature: signature,
                    recipientName: recipientName
                });
            }
        }
        onDone();
    }, [platform, carrierId, signature, takeContainer, onDone, occupancies]);

    const strokeWidth = useMemo<StrokeWidth>(() => {
        const defaultStroke: StrokeWidth = {
            minWidth: 1.5,
            maxWidth: 2.5
        };
        if (carrier) {
            return {
                minWidth: carrier.strokeMinWidth || defaultStroke.minWidth,
                maxWidth: carrier.strokeMinWidth || defaultStroke.maxWidth
            };
        }
        return defaultStroke;
    }, [carrier]);

    const outputFormat = useMemo<SignatureOutputFormat>(() => {
        const defaultOutputFormat: SignatureOutputFormat = "Png";
        if (carrier) {
            return carrier.signatureOutputFormat || defaultOutputFormat;
        }
        return defaultOutputFormat;
    }, [carrier]);

    const handleSignatureDone = useCallback(async (signature) => {
        setSignature(signature);
        const delivery = occupancies?.[0]?.content as DeliveryOccupancyContent;
        if (carrierId == "physical") {
            setStep(CustomerPickupSteps.ReUseContainer);
            return;
        }
        if (delivery.packageReleaseCodeRequired) {
            setStep(CustomerPickupSteps.PackageReleaseCode);
        } else {
            setStep(CustomerPickupSteps.BoxAccess);
        }
    }, [carrierId, setSignature, setStep, occupancies]);

    const handleReUseContainer = useCallback(async (takeContainer: boolean) => {
        setTakeContainer(takeContainer);
        const delivery = occupancies?.[0]?.content as DeliveryOccupancyContent;
        if (delivery.packageReleaseCodeRequired) {
            setStep(CustomerPickupSteps.PackageReleaseCode);
        } else {
            setStep(CustomerPickupSteps.BoxAccess);
        }
    }, [setTakeContainer, setStep, occupancies]);

    const handlePackageReleaseCodeDone = useCallback(async (packageReleaseCode: string) => {
        const delivery = occupancies?.[0]?.content as DeliveryOccupancyContent;

        try {
            setPackageReleaseCodeLoading(true);
            await customerPickupEventHandler.packageReleaseCodeConfirmed({
                cubeId: platform.hostname,
                carrierId: delivery.carrierId!,
                parcelId: delivery.deliveryId,
                packageReleaseCode: packageReleaseCode,
                signature,
                recipientName,
                backendApiCredentials: backendApiCredentials!,
            });
            setStep(CustomerPickupSteps.BoxAccess);
        } catch (err) {
            console.log(`Error during handling package release code for parcel: ${delivery.deliveryId}`, err);
            setMessage({
                title: t("PackageReleaseCodeFailedTitle"),
                message: t("PackageReleaseCodeFailedDescription"),
            });
        } finally {
            setPackageReleaseCodeLoading(false);
        }
    }, [occupancies, platform, signature, recipientName, backendApiCredentials]);

    const handleRecipientNameConfirmed = useCallback((name: string) => {
        setRecipientName(name);
        setStep(CustomerPickupSteps.Signature);
    }, [setStep, setRecipientName]);

    const handleBoxAccessDone = useCallback(async () => {
        await handleDone();
    }, [setStep, handleDone]);

    switch (step) {
        case CustomerPickupSteps.CustomerName:
            return (
                <RecipientNameView onCancel={onDone} onNext={handleRecipientNameConfirmed} carrierId={carrierId!} />
            );
        case CustomerPickupSteps.Signature:
            return (
                <SignatureView
                    strokeWidth={strokeWidth}
                    onNext={handleSignatureDone}
                    onCancel={onDone}
                    outputFormat={outputFormat}/>
            );
        case CustomerPickupSteps.ReUseContainer:
            return (
                <ReUseContainerView onNext={handleReUseContainer} onCancel={onDone} />
            );
        case CustomerPickupSteps.PackageReleaseCode:
            if (packageReleaseCodeLoading) {
                return (
                    <PendingView message={t("ProcessingPackageReleaseCode")} />
                );
            } else {
                return (
                    <PackageReleaseCodeView
                        onNext={handlePackageReleaseCodeDone}
                        onCancel={onDone}
                    />
                );
            }
        case CustomerPickupSteps.BoxAccess:
            return (
                <BoxAccess
                    boxes={occupiedBoxes!}
                    platform={platform}
                    onCancel={onDone}
                    onConfirm={handleBoxAccessDone}
                    boxOpenTitle={takeContainer ? t("PickupTitle") : t("LeaveInLockerPickupTitle")}
                    disableConfirm={carrier?.firstMile?.reopenCountdown ? carrier?.firstMile?.reopenCountdown <= 1 : false}
                    boxOpenMessage={takeContainer ? t("PickupMessage") : t("LeaveInLockerPickupMessage")}
                    confirmTitle={takeContainer ? t("ConfirmPickupTitle") : t("LeaveInLockerConfirmPickupTitle")}
                    confirmMessage={takeContainer ? t("ConfirmPickupMessage") : t("LeaveInLockerConfirmPickupMessage")}
                    strings={t}
                    timeout
                    autoConfirm={carrier?.lastMile?.reopenCountdown}
                />
            );
        default:
            return null;
    }
}

